import merge from 'lodash.merge';
import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectIsGoogleMapsLoaded } from 'redux/config/config.selectors';

export const usePlacesAutocompleteNew = (options = {}) => {
  const optionsRef = useRef(
    merge(
      {
        debounce: 750,
        requestOptions: {},
      },
      options,
    ),
  );
  const { debounce, requestOptions } = optionsRef.current;
  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const autocompleteRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const isGoogleMapsLoaded = useSelector(selectIsGoogleMapsLoaded);

  useEffect(() => {
    if (!isGoogleMapsLoaded) {
      /* istanbul ignore next */
      return;
    }

    if (!sessionTokenRef.current) {
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
    }

    // Create a dummy input element for Autocomplete
    const dummyInput = document.createElement('input');
    autocompleteRef.current = new window.google.maps.places.Autocomplete(
      dummyInput,
      {
        ...requestOptions,
      },
    );

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(
          autocompleteRef.current,
        );
      }
    };
  }, [requestOptions, isGoogleMapsLoaded]);

  useEffect(() => {
    if (!isGoogleMapsLoaded) {
      /* istanbul ignore next */
      return;
    }

    if (!query) {
      setPredictions([]);
      return;
    }

    const handler = setTimeout(async () => {
      if (!sessionTokenRef.current) {
        sessionTokenRef.current =
          new window.google.maps.places.AutocompleteSessionToken();
      }

      const request = {
        input: query,
        sessionToken: sessionTokenRef.current,
        ...requestOptions,
      };

      setLoading(true);
      const { suggestions } =
        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          request,
        );
      setPredictions(
        suggestions.filter(
          suggestion => suggestion.placePrediction.mainText?.text,
        ) || [],
      );
    }, debounce);

    return () => clearTimeout(handler);
  }, [query, debounce, requestOptions, isGoogleMapsLoaded]);

  const setValue = value => {
    setQuery(value);
  };

  const clearPredictions = () => setPredictions([]);

  return {
    suggestions: { data: predictions, loading },
    setValue,
    clearPredictions,
    sessionTokenRef,
  };
};
